import { useState } from "react";
import type { ImageData } from "../types";
import { toDataUrl, getInitials } from "./ImageUpload";

interface PlayerAvatarProps {
  player: {
    full_name?: string;
    name?: string;
    short_name?: string;
    photo_url?: string | null;
    photo_image?: ImageData | null;
  };
  size?: number;
  className?: string;
}

const PLAYER_COLORS = [
  "bg-slate-100 text-slate-700",
  "bg-zinc-100 text-zinc-700",
  "bg-stone-100 text-stone-700",
  "bg-sky-100 text-sky-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
  "bg-violet-100 text-violet-700",
  "bg-lime-100 text-lime-700",
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export default function PlayerAvatar({
  player,
  size = 32,
  className = "",
}: PlayerAvatarProps) {
  const [broken, setBroken] = useState(false);
  const displayName = player.full_name ?? player.name ?? "";
  const src = broken
    ? null
    : (player.photo_url ??
      (player.photo_image ? toDataUrl(player.photo_image) : null));

  if (src) {
    return (
      <img
        src={src}
        alt={displayName}
        width={size}
        height={size}
        className={`rounded-full object-cover ${className}`}
        data-testid="player-avatar-img"
        onError={() => setBroken(true)}
      />
    );
  }

  const initials = getInitials(displayName);
  const colorClass =
    PLAYER_COLORS[hashString(displayName) % PLAYER_COLORS.length];
  const fontSize =
    size < 24 ? "text-[8px]" : size < 32 ? "text-[10px]" : "text-xs";

  return (
    <div
      className={`flex items-center justify-center rounded-full font-semibold select-none ${colorClass} ${fontSize} ${className}`}
      style={{ width: size, height: size, minWidth: size }}
      data-testid="player-avatar-fallback"
      title={displayName}
    >
      {initials}
    </div>
  );
}

export type { PlayerAvatarProps };
